import { Sparkles, Trash2, Check, Loader2, AlertCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

/**
 * DuplicateRecipesDialog — viser AI-føreslegne duplikat-grupper.
 * Brukaren kan haka av / på for kva som skal slettast, så bekrefte.
 */
export default function DuplicateRecipesDialog({
  open,
  onOpenChange,
  loading,
  groups,
  stats,
  selected,
  onToggleSelected,
  onDelete,
  deleting,
}) {
  const selectedCount = Object.values(selected || {}).filter(Boolean).length;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !deleting) onOpenChange(false); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col p-0 gap-0" data-testid="duplicates-dialog">
        <DialogHeader className="p-4 pb-3 border-b border-stone-100">
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            <Sparkles className="h-5 w-5 text-violet-600" />
            AI-duplikatsjekk
          </DialogTitle>
          <DialogDescription>
            {loading
              ? "Analyserer alle oppskrifter…"
              : stats.total_duplicates === 0
                ? `Ingen duplikat funne blant ${stats.total_recipes} oppskrifter 🎉`
                : `Fann ${stats.total_duplicates} duplikat i ${groups.length} grupper (av ${stats.total_recipes} oppskrifter). Vel kva som skal fjernast — dei med best info er førehandsvalt som "behald".`}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12 text-stone-500">
              <Loader2 className="h-8 w-8 animate-spin mb-3 text-violet-600" />
              <p className="text-sm">AI samanliknar titlar og ingrediensar…</p>
            </div>
          ) : groups.length === 0 ? (
            <div className="text-center py-12 text-stone-500">
              <Check className="h-10 w-10 mx-auto mb-3 text-emerald-500" />
              <p className="text-sm">Alt ser reint ut!</p>
            </div>
          ) : (
            groups.map((g, idx) => (
              <div key={idx} className="rounded-lg border border-stone-200 bg-white p-3" data-testid={`dupe-group-${idx}`}>
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                    g.source === "rule"
                      ? "bg-emerald-100 text-emerald-800"
                      : "bg-violet-100 text-violet-800"
                  }`}>
                    {g.source === "rule" ? "Same tittel" : "AI-forslag"}
                  </span>
                  <span className="text-[11px] text-stone-500">{g.reason}</span>
                </div>
                {/* Keeper — always kept */}
                <div className="flex items-start gap-2 rounded-md bg-emerald-50 border border-emerald-100 px-2.5 py-2 mb-1.5" data-testid={`keeper-${g.keeper_id}`}>
                  <Check className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-stone-800 truncate">{g.keeper_title}</p>
                    <p className="text-[10px] text-emerald-700 font-semibold uppercase tracking-wide">Behald</p>
                  </div>
                </div>
                {/* Duplicates — checkboxes */}
                {g.duplicate_ids.map((dupId, i) => {
                  const dupTitle = g.duplicate_titles[i];
                  const isSelected = !!selected[dupId];
                  return (
                    <label
                      key={dupId}
                      className={`flex items-start gap-2 rounded-md border px-2.5 py-2 mt-1 cursor-pointer transition-colors ${
                        isSelected
                          ? "bg-red-50 border-red-200"
                          : "bg-stone-50 border-stone-200 hover:bg-stone-100"
                      }`}
                      data-testid={`dupe-${dupId}`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => onToggleSelected(dupId)}
                        className="mt-1 flex-shrink-0"
                        data-testid={`dupe-checkbox-${dupId}`}
                      />
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm ${isSelected ? "text-red-700 line-through" : "text-stone-700"} truncate`}>{dupTitle}</p>
                        {isSelected && (
                          <p className="text-[10px] text-red-600 font-semibold uppercase tracking-wide flex items-center gap-1">
                            <Trash2 className="h-3 w-3" /> Slett
                          </p>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            ))
          )}
        </div>

        {groups.length > 0 && !loading && (
          <div className="p-4 border-t border-stone-100 bg-stone-50 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-xs text-stone-600 flex items-center gap-1.5">
              <AlertCircle className="h-3.5 w-3.5 text-amber-600" />
              <span>Sletting er permanent — ingen angrar</span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => onOpenChange(false)}
                disabled={deleting}
                className="rounded-md border border-stone-200 bg-white px-3 py-2 text-sm text-stone-700 hover:bg-stone-50 disabled:opacity-50"
                data-testid="dupes-cancel"
              >
                Avbryt
              </button>
              <button
                onClick={onDelete}
                disabled={deleting || selectedCount === 0}
                className="rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50 flex items-center gap-1.5"
                data-testid="dupes-delete-btn"
              >
                {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                Slett {selectedCount} valt{selectedCount !== 1 ? "e" : ""}
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
